import { type AlgoliaRecord } from '../../../algolia'
import {
  type ProductDiscountBoundaries,
  productIsValidDiscount,
} from '../../../product'
import { TENANT } from '../../../tenant'

interface ProductSearchAlgoliaAccessorGetDiscountData {
  discountInPercent: number
  hasDiscount: boolean
  showDiscount: boolean
}

/*
  If the discount is smaller than 1% then we hide the discount for IT and FR tenants
 */
const hideDiscountBasedOnTenant = ({
  discountInPercent,
  tenant,
}: {
  discountInPercent: number
  tenant: TENANT
}): boolean => {
  if (Number.isNaN(discountInPercent)) {
    return false
  }

  const tenantsToHideSmallDiscount = [TENANT.IT, TENANT.FR]
  const isTenantToHide = new Set<TENANT>(tenantsToHideSmallDiscount).has(tenant)

  return isTenantToHide && discountInPercent <= 1
}

/*
  We need discount information and also to validate the discount before showing it to the user.
  We also factor in if the discount has to be hidden, even tough it could be still available.
  showDiscount flag checks for the validity and configuration of discount for the product. If that is true then we are safe to show the discount on the SERP Result Item card
 */
export const productSearchAlgoliaAccessorGetDiscountData = (
  algoliaProduct: AlgoliaRecord,
  discountBoundaries: ProductDiscountBoundaries | undefined,
  tenant: TENANT,
): ProductSearchAlgoliaAccessorGetDiscountData => {
  const {
    discountInPercent,
    hide_discounts: hideDiscounts,
    prescriptionMedicine,
  } = algoliaProduct
  const hasDiscount = discountInPercent > 0 && !hideDiscounts
  const productHasValidDiscount = productIsValidDiscount(
    discountInPercent,
    discountBoundaries,
  )

  const showDiscount =
    !hideDiscountBasedOnTenant({ discountInPercent, tenant }) &&
    hasDiscount &&
    !prescriptionMedicine &&
    productHasValidDiscount

  return {
    discountInPercent,
    hasDiscount,
    showDiscount,
  }
}
